import mockReleaseData from "../data/mockData/releasesData";
import mockReleaseDetailsData from "../data/mockData/releaseDetailsData";
import { getDetail } from "./listQueryUtils";

const RELEASE_LIST_KEY = "wl_bs_releases_extras";
const RELEASE_DETAILS_KEY = "wl_bs_release_details_by_id";

function readJson(key, fallback) {
  if (typeof sessionStorage === "undefined") {
    return fallback;
  }
  try {
    const raw = sessionStorage.getItem(key);
    if (raw == null) {
      return fallback;
    }
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.setItem(key, JSON.stringify(value));
}

function mergeById(baseRows, extras) {
  const map = new Map();
  (baseRows || []).forEach(row => {
    if (row?.id != null) {
      map.set(String(row.id), row);
    }
  });
  (extras || []).forEach(row => {
    if (row?.id != null) {
      map.set(String(row.id), { ...map.get(String(row.id)), ...row });
    }
  });
  return Array.from(map.values());
}

function getMergedReleaseDetail(releaseId) {
  const sid = String(releaseId);
  const listExtras = readJson(RELEASE_LIST_KEY, []);
  const details = readJson(RELEASE_DETAILS_KEY, {});
  const listRows = mergeById(mockReleaseData, listExtras);
  const listRow = getDetail(listRows, releaseId) || {};

  if (details[sid]) {
    return { ...details[sid] };
  }

  const seedDetail = getDetail(mockReleaseDetailsData, releaseId);
  if (seedDetail) {
    return { ...seedDetail, ...listRow, id: seedDetail.id ?? releaseId };
  }

  return { ...listRow, id: Number(releaseId) || releaseId };
}

function saveReleaseDetail(releaseId, patch) {
  const sid = String(releaseId);
  const details = readJson(RELEASE_DETAILS_KEY, {});
  const listExtras = readJson(RELEASE_LIST_KEY, []);
  const current = getMergedReleaseDetail(releaseId);
  const next = { ...current, ...(patch || {}), id: Number(releaseId) || releaseId };

  details[sid] = next;
  writeJson(RELEASE_DETAILS_KEY, details);

  const listRows = mergeById(mockReleaseData, listExtras);
  const baseList = listRows.find(row => String(row.id) === sid) || { id: next.id };
  const listPatch = {
    ...baseList,
    ...(patch?.pre_scan_done !== undefined ? { pre_scan_done: patch.pre_scan_done } : {}),
    ...(patch?.pre_scan !== undefined ? { pre_scan: patch.pre_scan } : {}),
    ...(patch?.release_pre_scan_declaration !== undefined
      ? { release_pre_scan_declaration: patch.release_pre_scan_declaration }
      : {}),
    ...(patch?.status !== undefined ? { status: patch.status } : {}),
    ...(patch?.all_tracks_ready !== undefined ? { all_tracks_ready: patch.all_tracks_ready } : {}),
  };

  const listIdx = listExtras.findIndex(row => String(row.id) === sid);
  if (listIdx >= 0) {
    listExtras[listIdx] = listPatch;
  } else {
    listExtras.push(listPatch);
  }
  writeJson(RELEASE_LIST_KEY, listExtras);

  return next;
}

function buildCleanPreScanResult(releaseId, tracks) {
  const preScanId = Number(releaseId) * 100 + 7;
  return {
    id: preScanId,
    status: "completed",
    alert: false,
    admin_declaration: null,
    scanned_tracks: (tracks || []).map(track => ({
      id: track.id,
      track_name: track.name || "Track",
      alert: false,
      music_results: [],
      cover_results: [],
    })),
  };
}

/** Simulates POST releases/perform-pre-scan/ — no real ACR API. */
export function handlePerformDemoPreScan(releaseId) {
  if (releaseId == null || releaseId === "") {
    throw new Error("Release id is required.");
  }

  const detail = getMergedReleaseDetail(releaseId);
  const tracks = Array.isArray(detail.tracks) ? detail.tracks : [];
  const pre_scan = buildCleanPreScanResult(releaseId, tracks);

  return saveReleaseDetail(releaseId, {
    pre_scan_done: true,
    pre_scan,
    release_pre_scan_declaration: null,
  });
}

function findReleaseIdByPreScanId(preScanId) {
  const listExtras = readJson(RELEASE_LIST_KEY, []);
  const details = readJson(RELEASE_DETAILS_KEY, {});
  const listRows = mergeById(mockReleaseData, listExtras);
  const seen = new Set();

  const checkRelease = (release) => {
    if (!release?.id || seen.has(String(release.id))) {
      return null;
    }
    seen.add(String(release.id));
    if (String(release.pre_scan?.id) === String(preScanId)) {
      return release.id;
    }
    return null;
  };

  for (const release of Object.values(details)) {
    const hit = checkRelease(release);
    if (hit != null) {
      return hit;
    }
  }

  for (const release of mockReleaseDetailsData) {
    const hit = checkRelease(release);
    if (hit != null) {
      return hit;
    }
  }

  for (const release of listRows) {
    const merged = getMergedReleaseDetail(release.id);
    const hit = checkRelease(merged);
    if (hit != null) {
      return hit;
    }
  }

  return null;
}

/** Simulates POST release-acr-deep-scan/ — admin pre-scan declaration. */
export function handleDemoPreScanDeclaration(preScanId, adminDeclaration) {
  if (preScanId == null || preScanId === "") {
    throw new Error("Pre-scan id is required.");
  }

  const releaseId = findReleaseIdByPreScanId(preScanId);
  if (releaseId == null) {
    throw new Error("Pre-scan not found.");
  }

  const detail = getMergedReleaseDetail(releaseId);
  const adminDec = Number(adminDeclaration);

  if (adminDec === 1) {
    return saveReleaseDetail(releaseId, {
      pre_scan: {
        ...(detail.pre_scan || {}),
        admin_declaration: 1,
      },
      release_pre_scan_declaration: {
        status: "rejected",
        admin_message: "Release cancelled per pre-scan declaration.",
      },
      status: "offline",
    });
  }

  if (adminDec === 2) {
    return saveReleaseDetail(releaseId, {
      pre_scan: {
        ...(detail.pre_scan || {}),
        admin_declaration: 2,
      },
      release_pre_scan_declaration: {
        status: "approved",
        admin_message: "Pre-scan declaration approved. You may proceed with distribution.",
      },
    });
  }

  throw new Error("Invalid pre-scan declaration.");
}

/** Simulates POST release-acr-scan/ — user copyright declaration (no real scan API). */
export function handleDemoAcrScanDeclaration(payload = {}) {
  const scanId = payload.id;
  if (scanId == null || scanId === "") {
    throw new Error("Scan id is required.");
  }

  return {
    id: scanId,
    status: "ok",
    user_declaration: Number(payload.user_declaration) || 2,
  };
}

export function releaseExistsForDemoPreScan(releaseId) {
  if (releaseId == null || releaseId === "") {
    return false;
  }
  const sid = String(releaseId);
  const listExtras = readJson(RELEASE_LIST_KEY, []);
  const details = readJson(RELEASE_DETAILS_KEY, {});
  if (details[sid]) {
    return true;
  }
  if (getDetail(mockReleaseDetailsData, releaseId)) {
    return true;
  }
  if (getDetail(mockReleaseData, releaseId)) {
    return true;
  }
  return listExtras.some(row => String(row.id) === sid);
}
